import { useEntityStore } from './entity-store';
import type { BaseEntityRow, BaseEntityMapKey, CollectionRow } from './entity-store';
import type { WorkspaceDoc, WorkspaceCollection } from './entity-types';
import type { Access } from '@/lib/sharing/utils/permissions';

/**
 * Subset of `BASE_ENTITY_MAP_KEYS` whose contents appear in the Documents view
 * (the `WorkspaceDoc[]` projection). Collections render in their own surface;
 * spaces are containers (not content) and have their own surface too.
 *
 * The `satisfies` clause makes this a compile-time error if any entry isn't a
 * real `BaseEntityMapKey`. Adding a new doc-like kind = (1) add the Map field
 * on the store, (2) list it in `BASE_ENTITY_MAP_KEYS`, (3) list it here.
 * The projection rebuild loop is generic over this list.
 */
const DOC_LIKE_MAP_KEYS = [
  'entityMeta',
  'canvases',
] as const satisfies ReadonlyArray<BaseEntityMapKey>;

// Optimistic rows created locally before the server's UPSERTED event arrives
// don't carry an `access` payload. Treat them as owner — the creator owns the
// entity, and the server will overwrite this on the next sync round-trip.
const OPTIMISTIC_OWNER_ACCESS: Access = Object.freeze({
  permission: 'owner',
  grantedAt: '',
  grantedBy: null,
  via: 'direct',
  capabilities: { view: true, edit: true, manage: true, delete: true },
  removeSelf: false,
  removeSelfItemCount: null,
});

export type EntitySnapshot = {
  readonly docs: readonly WorkspaceDoc[];
  readonly collections: readonly WorkspaceCollection[];
};

const EMPTY_SNAPSHOT: EntitySnapshot = Object.freeze({
  docs: Object.freeze([]) as readonly WorkspaceDoc[],
  collections: Object.freeze([]) as readonly WorkspaceCollection[],
});

export class EntityProjection {
  private snapshot: EntitySnapshot = EMPTY_SNAPSHOT;
  private listeners = new Set<() => void>();
  private unsubscribeStore: (() => void) | null = null;

  // Track previous doc IDs for deletion detection
  private previousDocIds = new Set<string>();

  // Remote deletion detection callback
  private onDocDisappeared: ((id: string) => void) | null = null;

  // True while a server bootstrap is in progress (resetEntities → applyBatch).
  // Deletion detection is suppressed until the bootstrap completes so that the
  // transient empty-store state does not produce false "doc disappeared" fires.
  private bootstrapInFlight = false;

  setOnDocDisappeared(fn: ((id: string) => void) | null): void {
    this.onDocDisappeared = fn;
  }

  /** Call immediately before resetEntities() + applyBatch() during server bootstrap. */
  notifyBootstrapStart(): void {
    this.bootstrapInFlight = true;
  }

  /** Call after the bootstrap applyBatch() completes. */
  notifyBootstrapEnd(): void {
    this.bootstrapInFlight = false;
    // Sync previousDocIds to current state across every doc-like Map so the
    // next real update diffs correctly.
    const state = useEntityStore.getState();
    const seed = new Set<string>();
    for (const m of docMapsFromState(state)) for (const id of m.keys()) seed.add(id);
    this.previousDocIds = seed;
  }

  /** Attach to entityStore — no parameters needed (store is global). */
  attach(): void {
    this.detach();

    // Subscribe to store changes
    this.unsubscribeStore = useEntityStore.subscribe((state) => {
      this.rebuild(docMapsFromState(state), state.collections);
    });

    // Seed initial snapshot from current store state
    const state = useEntityStore.getState();
    this.rebuild(docMapsFromState(state), state.collections);
  }

  detach(): void {
    this.unsubscribeStore?.();
    this.unsubscribeStore = null;
    this.snapshot = EMPTY_SNAPSHOT;
    this.previousDocIds.clear();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): EntitySnapshot {
    return this.snapshot;
  }

  private rebuild(
    docMaps: ReadonlyArray<Map<string, BaseEntityRow>>,
    collectionsMap: Map<string, CollectionRow>,
  ): void {
    // Deletion detection: find doc IDs that were present before but are gone now.
    // Skip during bootstrap (resetEntities → applyBatch sequence) — the transient
    // empty state between those two calls must not fire false disappearances.
    const currentDocIds = new Set<string>();
    for (const m of docMaps) for (const id of m.keys()) currentDocIds.add(id);

    if (!this.bootstrapInFlight && this.onDocDisappeared) {
      for (const id of this.previousDocIds) {
        if (!currentDocIds.has(id)) {
          this.onDocDisappeared(id);
        }
      }
    }
    if (!this.bootstrapInFlight) {
      this.previousDocIds = currentDocIds;
    }

    const allEntries: WorkspaceDoc[] = [];
    for (const m of docMaps) {
      for (const [id, row] of m) allEntries.push(projectBaseRow(id, row));
    }

    const docs = Object.freeze(
      allEntries.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    );

    const collections = Object.freeze(
      Array.from(collectionsMap.entries())
        .map(([id, row]) => projectCollection(id, row))
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    );

    this.snapshot = Object.freeze({ docs, collections });
    this.listeners.forEach((l) => l());
  }
}

// ---------------------------------------------------------------------------
// Projection helpers
// ---------------------------------------------------------------------------

/**
 * Returns the doc-like subset of BaseEntityRow Maps from the current state,
 * generic over `DOC_LIKE_MAP_KEYS` so adding a future doc-like kind only
 * requires updating that constant — the rebuild loop adapts automatically.
 */
function docMapsFromState(state: ReturnType<typeof useEntityStore.getState>):
  ReadonlyArray<Map<string, BaseEntityRow>> {
  return DOC_LIKE_MAP_KEYS.map((k) => state[k] as Map<string, BaseEntityRow>);
}

/**
 * Project a BaseEntityRow into the WorkspaceDoc shape consumed by Documents
 * view. Kind-agnostic — reads `row.kind` directly. Replaces the per-kind
 * `projectDoc` / `projectCanvas` helpers (which were structurally identical
 * except for the kind literal).
 */
function projectBaseRow(id: string, row: BaseEntityRow): WorkspaceDoc {
  return Object.freeze({
    id,
    title: row.title ?? '',
    snippet: row.snippet ?? '',
    kind: row.kind as WorkspaceDoc['kind'],
    tags: Object.freeze(row.tags ?? []) as readonly string[],
    isArchived: row.isArchived ?? false,
    updatedAt: row.updatedAt ?? '',
    collectionIds: Object.freeze(row.collectionIds ?? []) as readonly string[],
    previewMarkup: row.previewMarkup ?? null,
    previewKind: row.previewKind ?? null,
    access: row.access ?? OPTIMISTIC_OWNER_ACCESS,
  });
}

function projectCollection(id: string, row: CollectionRow): WorkspaceCollection {
  return Object.freeze({
    id,
    name: row.title ?? '',
    tags: row.tags ?? [],
    isArchived: row.isArchived ?? false,
    createdAt: row.createdAt ?? '',
    updatedAt: row.updatedAt ?? '',
    access: row.access ?? OPTIMISTIC_OWNER_ACCESS,
  });
}

/** Singleton projection instance shared across the app. */
export const entityProjection = new EntityProjection();

// Recording hooks live in @features/recordings/hooks/ as of session 21.
// The session-9 versions that previously lived here (filterless useRecordings,
// minimal useRecording) had zero callers and were deleted to avoid a footgun
// where a careless import would bypass the new hooks' filter / sort semantics.
